import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Avatar,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Send as SendIcon,
  AttachFile,
  PersonAdd,
  AddCircle,
  SmartToy,
  LocalHospital,
  Person,
  Support,
  Science,
  Analytics,
  Event
} from '@mui/icons-material';

const LandingChatWindow = ({ isOpen, onClose, onMinimize, mainPanelRef, topicContext }) => {
  const ollamaBaseUrl = process.env.REACT_APP_OLLAMA_BASE_URL || 'http://localhost:11434';
  const ollamaModel = process.env.REACT_APP_OLLAMA_MODEL || 'llama3.2:latest';
  const [chatDimensions, setChatDimensions] = useState({ width: 350, height: 450 });
  const [chatPosition, setChatPosition] = useState({ right: 20, bottom: 20 });
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      text: "Hi! I'm MammoAssist, your AI guide to breast cancer clinical trials. I can help you learn about our program and available trials!",
      timestamp: new Date().toISOString()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(ollamaModel);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [chatParticipants, setChatParticipants] = useState([]);
  const [activeAgents, setActiveAgents] = useState(['MammoAssist']);
  const [activeTopicAgent, setActiveTopicAgent] = useState('MammoAssist');
  const [chatThemeColor, setChatThemeColor] = useState('primary');
  const [addUserMenuAnchor, setAddUserMenuAnchor] = useState(null);
  const [addAgentMenuAnchor, setAddAgentMenuAnchor] = useState(null);
  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const abortedByUserRef = useRef(false);
  const hasUserResizedRef = useRef(false);
  const hasUserMovedRef = useRef(false);
  const resizingRef = useRef(false);
  const draggingRef = useRef(false);

  useEffect(() => {
    const updateChatSize = () => {
      if (mainPanelRef?.current) {
        const mainPanelRect = mainPanelRef.current.getBoundingClientRect();
        const chatWidth = Math.floor(mainPanelRect.width / 3); // 1/3 of main panel width
        const chatHeight = Math.floor(mainPanelRect.height / 3); // 1/3 of main panel height

        if (!hasUserResizedRef.current) {
          setChatDimensions({
            width: Math.max(chatWidth, 300), // Minimum 300px width
            height: Math.max(chatHeight, 400) // Minimum 400px height
          });
        } else {
          const maxWidth = Math.max(320, Math.floor(mainPanelRect.width - 40));
          const maxHeight = Math.max(420, Math.floor(mainPanelRect.height - 40));
          setChatDimensions((prev) => ({
            width: Math.min(Math.max(prev.width, 300), maxWidth),
            height: Math.min(Math.max(prev.height, 400), maxHeight)
          }));
        }

        if (!hasUserMovedRef.current) {
          setChatPosition({ right: 20, bottom: 20 });
        } else {
          const maxRight = Math.max(0, Math.floor(mainPanelRect.width - 40));
          const maxBottom = Math.max(0, Math.floor(mainPanelRect.height - 40));
          setChatPosition((prev) => ({
            right: Math.min(Math.max(prev.right, 0), maxRight),
            bottom: Math.min(Math.max(prev.bottom, 0), maxBottom)
          }));
        }
      }
    };

    updateChatSize();
    window.addEventListener('resize', updateChatSize);
    
    return () => window.removeEventListener('resize', updateChatSize);
  }, [mainPanelRef]);

  useEffect(() => {
    if (!isLoading) {
      setElapsedSeconds(0);
      return;
    }

    const intervalId = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isLoading]);

  useEffect(() => {
    if (!messagesContainerRef.current) {
      return;
    }
    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
  }, [messages, isLoading, elapsedSeconds]);

  useEffect(() => () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isDisposed = false;

    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const response = await fetch(`${ollamaBaseUrl}/api/tags`);
        if (!response.ok) {
          throw new Error(`Failed to load Ollama models (${response.status})`);
        }

        const data = await response.json();
        const modelNames = (data?.models || []).map((model) => model.name).filter(Boolean);
        if (isDisposed) {
          return;
        }

        setAvailableModels(modelNames);
        if (modelNames.length === 0) {
          setSelectedModel(ollamaModel);
          return;
        }

        setSelectedModel((prevSelectedModel) => {
          if (modelNames.includes(prevSelectedModel)) {
            return prevSelectedModel;
          }
          if (modelNames.includes(ollamaModel)) {
            return ollamaModel;
          }
          return modelNames[0];
        });
      } catch (error) {
        if (!isDisposed) {
          setAvailableModels([]);
        }
        console.error('Failed loading Ollama models:', error);
      } finally {
        if (!isDisposed) {
          setModelsLoading(false);
        }
      }
    };

    loadModels();

    return () => {
      isDisposed = true;
    };
  }, [isOpen, ollamaBaseUrl, ollamaModel]);

  const resolveModelForRequest = () => {
    if (selectedModel && availableModels.includes(selectedModel)) {
      return selectedModel;
    }
    if (availableModels.includes(ollamaModel)) {
      return ollamaModel;
    }
    if (availableModels.length > 0) {
      return availableModels[0];
    }
    return selectedModel || ollamaModel;
  };

  const constrainAssistantResponse = (text) => {
    const normalized = (text || '').replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return normalized;
    }
    const words = normalized.split(' ');
    if (words.length <= 50) {
      return normalized;
    }
    return `${words.slice(0, 50).join(' ')}...`;
  };

  const stopCurrentRequest = () => {
    if (!abortControllerRef.current || !isLoading) {
      return;
    }
    abortedByUserRef.current = true;
    abortControllerRef.current.abort();
  };

  const handleResizeStart = (event, resizeMode = 'corner') => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = chatDimensions.width;
    const startHeight = chatDimensions.height;

    resizingRef.current = true;

    const onMouseMove = (moveEvent) => {
      if (!resizingRef.current) {
        return;
      }

      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const mainPanelRect = mainPanelRef?.current?.getBoundingClientRect();
      const maxWidth = mainPanelRect ? Math.max(320, Math.floor(mainPanelRect.width - 40)) : 1000;
      const maxHeight = mainPanelRect ? Math.max(420, Math.floor(mainPanelRect.height - 40)) : 1000;

      const nextWidth = resizeMode === 'bottom'
        ? startWidth
        : Math.min(Math.max(startWidth + deltaX, 300), maxWidth);
      const nextHeight = resizeMode === 'right'
        ? startHeight
        : Math.min(Math.max(startHeight + deltaY, 400), maxHeight);

      hasUserResizedRef.current = true;
      setChatDimensions({ width: nextWidth, height: nextHeight });
    };

    const onMouseUp = () => {
      resizingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleDragStart = (event) => {
    if (resizingRef.current) {
      return;
    }

    const startX = event.clientX;
    const startY = event.clientY;
    const startRight = chatPosition.right;
    const startBottom = chatPosition.bottom;

    draggingRef.current = true;

    const onMouseMove = (moveEvent) => {
      if (!draggingRef.current) {
        return;
      }

      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const mainPanelRect = mainPanelRef?.current?.getBoundingClientRect();
      const maxRight = mainPanelRect ? Math.max(0, Math.floor(mainPanelRect.width - chatDimensions.width)) : 2000;
      const maxBottom = mainPanelRect ? Math.max(0, Math.floor(mainPanelRect.height - chatDimensions.height)) : 2000;

      const nextRight = Math.min(Math.max(startRight - deltaX, 0), maxRight);
      const nextBottom = Math.min(Math.max(startBottom - deltaY, 0), maxBottom);

      hasUserMovedRef.current = true;
      setChatPosition({ right: nextRight, bottom: nextBottom });
    };

    const onMouseUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleSend = async (externalMessage) => {
    const outgoingMessage = typeof externalMessage === 'string' ? externalMessage : message;
    const trimmedMessage = (outgoingMessage || '').trim();
    if (!trimmedMessage || isLoading) {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmedMessage,
      timestamp: new Date().toISOString()
    };

    const conversationHistory = [
      {
        role: 'system',
        content: 'You are MammoAssist. Answer each user question in 30 to 50 words. Be clear, factual, and concise. Avoid bullet lists unless requested.'
      },
      ...[...messages, userMessage].map((entry) => ({
      role: entry.role,
      content: entry.text
      }))
    ];

    setMessages((prev) => [...prev, userMessage]);
    if (!externalMessage) {
      setMessage('');
    }
    setIsLoading(true);
    setLoadingPhase('connecting');
    abortedByUserRef.current = false;

    const modelToUse = resolveModelForRequest();

    const assistantMessageId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: assistantMessageId,
        role: 'assistant',
        text: '',
        timestamp: new Date().toISOString()
      }
    ]);

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await fetch(`${ollamaBaseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal,
        body: JSON.stringify({
          model: modelToUse,
          messages: conversationHistory,
          stream: true
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Ollama request failed (${response.status})`);
      }

      if (!response.body) {
        throw new Error('Streaming response not available from Ollama.');
      }

      setLoadingPhase('generating');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let streamBuffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        streamBuffer += decoder.decode(value, { stream: true });
        const lines = streamBuffer.split('\n');
        streamBuffer = lines.pop() || '';

        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line) {
            continue;
          }

          let parsed;
          try {
            parsed = JSON.parse(line);
          } catch (parseError) {
            continue;
          }

          const nextChunk = parsed?.message?.content || '';
          if (nextChunk) {
            accumulatedText += nextChunk;
            setMessages((prev) => prev.map((entry) => (
              entry.id === assistantMessageId
                ? { ...entry, text: accumulatedText, timestamp: new Date().toISOString() }
                : entry
            )));
          }
        }
      }

      const constrainedText = constrainAssistantResponse(accumulatedText);
      if (constrainedText !== accumulatedText) {
        setMessages((prev) => prev.map((entry) => (
          entry.id === assistantMessageId
            ? { ...entry, text: constrainedText, timestamp: new Date().toISOString() }
            : entry
        )));
      }

      if (!accumulatedText.trim()) {
        setMessages((prev) => prev.map((entry) => (
          entry.id === assistantMessageId
            ? {
              ...entry,
              text: 'I did not receive a complete response. Please try again.',
              timestamp: new Date().toISOString()
            }
            : entry
        )));
      }
    } catch (error) {
      const isAbortError = error?.name === 'AbortError';
      const fallbackText = isAbortError && abortedByUserRef.current
        ? 'Response stopped. You can send a new question.'
        : `I couldn't reach local Ollama. Please make sure Ollama is running on ${ollamaBaseUrl} and model ${modelToUse} is available.`;

      setMessages((prev) => {
        const lastAssistantIndex = [...prev].reverse().findIndex((entry) => entry.role === 'assistant' && !entry.text.trim());
        if (lastAssistantIndex !== -1) {
          const targetIndex = prev.length - 1 - lastAssistantIndex;
          return prev.map((entry, index) => (
            index === targetIndex
              ? { ...entry, text: fallbackText, timestamp: new Date().toISOString() }
              : entry
          ));
        }

        return [
          ...prev,
          {
            id: `assistant-error-${Date.now()}`,
            role: 'assistant',
            text: fallbackText,
            timestamp: new Date().toISOString()
          }
        ];
      });
      console.error('Landing chat Ollama error:', error);
    } finally {
      abortControllerRef.current = null;
      setLoadingPhase('');
      setIsLoading(false);
      abortedByUserRef.current = false;
    }
  };

  useEffect(() => {
    if (!isOpen || !topicContext?.title) {
      return;
    }

    setActiveTopicAgent(topicContext.title);
    setChatThemeColor(topicContext.color || 'primary');
    setMessages((prev) => {
      const introText = `You are now chatting with the ${topicContext.title} agent. Ask any question about this topic and I'll help you explore it.`;
      const alreadyExists = prev.some((entry) => entry.role === 'assistant' && entry.text === introText);
      if (alreadyExists) {
        return prev;
      }
      return [
        ...prev,
        {
          id: `topic-intro-${Date.now()}`,
          role: 'assistant',
          text: introText,
          timestamp: new Date().toISOString()
        }
      ];
    });
    setMessage(`Tell me about ${topicContext.title.toLowerCase()}...`);
  }, [topicContext, isOpen]);

  const formatMessageTime = (timestamp) => new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    setAttachedFiles(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddUser = (userType) => {
    const newParticipant = {
      id: Date.now(),
      name: `${userType.charAt(0).toUpperCase() + userType.slice(1)} User`,
      type: userType
    };
    setChatParticipants(prev => [...prev, newParticipant]);
    setAddUserMenuAnchor(null);
  };

  const handleAddAgent = (agentType) => {
    const agentName = `${agentType.charAt(0).toUpperCase() + agentType.slice(1)} Agent`;
    if (!activeAgents.includes(agentName)) {
      setActiveAgents(prev => [...prev, agentName]);
    }
    setAddAgentMenuAnchor(null);
  };

  const removeParticipant = (participantId) => {
    setChatParticipants(prev => prev.filter(p => p.id !== participantId));
  };

  const removeAgent = (agentName) => {
    if (agentName !== 'MammoAssist') { // Don't allow removing main agent
      setActiveAgents(prev => prev.filter(agent => agent !== agentName));
    }
  };

  const suggestedQuestions = [
    "What clinical trials are available?",
    "How does the matching process work?",
    "What are the eligibility requirements?",
    "Tell me about MammoTrials"
  ];

  const getLoadingLabel = () => {
    if (loadingPhase === 'connecting') {
      return elapsedSeconds >= 4
        ? `Connecting to MammoAssist… (${elapsedSeconds}s)`
        : 'Connecting to MammoAssist…';
    }
    if (loadingPhase === 'generating') {
      return elapsedSeconds >= 8
        ? `Generating response… still working (${elapsedSeconds}s)`
        : 'Generating response…';
    }
    return 'MammoAssist is thinking…';
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: chatPosition.bottom,
        right: chatPosition.right,
        width: chatDimensions.width,
        height: chatDimensions.height,
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        border: '2px solid #1976d2',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        transform: 'translateY(0)',
        transition: 'all 0.3s ease'
      }}
    >
      {/* Chat Header */}
      <Box sx={{
        backgroundColor: `${chatThemeColor}.main`,
        color: 'white',
        p: 1.5,
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 50,
        cursor: 'move'
      }}
      onMouseDown={handleDragStart}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.2)' }}>
            <SmartToy fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
              {activeTopicAgent} Agent
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ 
                width: 6, 
                height: 6, 
                borderRadius: '50%', 
                bgcolor: '#4caf50' 
              }} />
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                Knowledge Assistant Online
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Feature indicators */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {/* Active Agents Display */}
          {activeAgents.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {activeAgents.slice(1).map((agent, index) => (
                <Chip
                  key={agent}
                  label={agent.replace(' Agent', '')}
                  size="small"
                  variant="outlined"
                  deleteIcon={<CloseIcon sx={{ fontSize: 10 }} />}
                  onDelete={() => removeAgent(agent)}
                  sx={{ 
                    height: 16, 
                    fontSize: '0.6rem',
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' }
                  }}
                />
              ))}
            </Box>
          )}

          {/* Chat Participants Display */}
          {chatParticipants.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {chatParticipants.map((participant) => (
                <Chip
                  key={participant.id}
                  label={participant.name}
                  size="small"
                  variant="outlined"
                  deleteIcon={<CloseIcon sx={{ fontSize: 10 }} />}
                  onDelete={() => removeParticipant(participant.id)}
                  sx={{ 
                    height: 16, 
                    fontSize: '0.6rem',
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' }
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Add User Button */}
          <Tooltip title="Add user to chat">
            <IconButton 
              size="small" 
              onClick={(e) => setAddUserMenuAnchor(e.currentTarget)}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ color: 'white', p: 0.5 }}
              aria-label="Add user to chat"
            >
              <PersonAdd fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Add Agent Button */}
          <Tooltip title="Add AI agent">
            <IconButton 
              size="small" 
              onClick={(e) => setAddAgentMenuAnchor(e.currentTarget)}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ color: 'white', p: 0.5 }}
              aria-label="Add AI agent to chat"
            >
              <AddCircle fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <IconButton size="small" sx={{ color: 'white', p: 0.5 }} onMouseDown={(e) => e.stopPropagation()} onClick={onMinimize}>
            <MinimizeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'white', p: 0.5 }} onMouseDown={(e) => e.stopPropagation()} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Chat Messages Area */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        p: 1.5,
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }} ref={messagesContainerRef}>
        <Box sx={{
          alignSelf: 'flex-start',
          maxWidth: '85%',
          bgcolor: '#f3f6fb',
          p: 1,
          borderRadius: '10px',
          border: '1px solid #d9e2f3'
        }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            🔹 Click 👤+ to invite others to the conversation<br/>
            🔹 Click ⊕ to add specialized AI agents<br/>
            🔹 Click 📎 to attach files
          </Typography>
        </Box>

        {/* Suggested Questions */}
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Try asking about:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {suggestedQuestions.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion}
                size="small"
                variant="outlined"
                clickable
                onClick={() => setMessage(suggestion)}
                disabled={isLoading}
                sx={{ 
                  alignSelf: 'flex-start',
                  fontSize: '0.75rem',
                  height: 24,
                  '&:hover': { bgcolor: 'primary.light', color: 'white' }
                }}
              />
            ))}
          </Box>
        </Box>

        {messages.map((chatMessage) => {
          const isUser = chatMessage.role === 'user';
          return (
            <Box
              key={chatMessage.id}
              sx={{
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                bgcolor: isUser ? 'primary.main' : 'white',
                color: isUser ? 'white' : 'text.primary',
                p: 1.5,
                borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                boxShadow: 1
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                {chatMessage.text}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  display: 'block',
                  color: isUser ? 'rgba(255,255,255,0.8)' : 'text.secondary'
                }}
              >
                {formatMessageTime(chatMessage.timestamp)}
              </Typography>
            </Box>
          );
        })}

        {isLoading && (
          <Box
            sx={{
              alignSelf: 'flex-start',
              maxWidth: '85%',
              bgcolor: 'white',
              p: 1.5,
              borderRadius: '12px 12px 12px 4px',
              boxShadow: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                {getLoadingLabel()}
              </Typography>
              <Chip
                label="Stop"
                size="small"
                variant="outlined"
                onClick={stopCurrentRequest}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Chat Input */}
      <Box sx={{
        p: 1.5,
        borderTop: '1px solid #e0e0e0',
        backgroundColor: 'white'
      }}>
        <Box sx={{ mb: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="landing-chat-model-label" sx={{ fontSize: '0.8rem' }}>Model</InputLabel>
            <Select
              labelId="landing-chat-model-label"
              value={selectedModel}
              label="Model"
              onChange={(event) => setSelectedModel(event.target.value)}
              disabled={isLoading || modelsLoading || availableModels.length === 0}
              sx={{
                fontSize: '0.8rem',
                '& .MuiSelect-select': { py: 0.75 }
              }}
            >
              {(availableModels.length > 0 ? availableModels : [selectedModel]).map((modelName) => (
                <MenuItem key={modelName} value={modelName} sx={{ fontSize: '0.8rem' }}>
                  {modelName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {availableModels.length === 0 && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              No Ollama models detected yet. Pull a model to enable selection.
            </Typography>
          )}
        </Box>

        {/* File Attachments Display */}
        {attachedFiles.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
              Attachments:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {attachedFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name.length > 20 ? `${file.name.substring(0, 17)}...` : file.name}
                  size="small"
                  variant="outlined"
                  deleteIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                  onDelete={() => removeAttachment(index)}
                  sx={{ 
                    height: 20, 
                    fontSize: '0.6rem',
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          {/* File Attachment Button */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileAttachment}
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.csv,.xlsx,.xls"
          />
          <Tooltip title="Attach files (PDF, DOC, images, CSV, etc.)">
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{ 
                mb: 0.25,
                color: attachedFiles.length > 0 ? 'primary.main' : 'text.secondary',
                '&:hover': { color: 'primary.main' }
              }}
              aria-label="Attach files"
            >
              <AttachFile sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          
          <TextField
            fullWidth
            size="small"
            placeholder="Ask about clinical trials..."
            variant="outlined"
            multiline
            maxRows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.85rem'
              },
              '& .MuiInputBase-input': {
                py: 1
              }
            }}
          />
          <IconButton 
            size="small" 
            onClick={handleSend}
            disabled={!message.trim() || isLoading}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              width: 32,
              height: 32,
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'grey.300' }
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Add User Menu */}
      <Menu
        anchorEl={addUserMenuAnchor}
        open={Boolean(addUserMenuAnchor)}
        onClose={() => setAddUserMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleAddUser('doctor')}>
          <ListItemIcon><LocalHospital fontSize="small" /></ListItemIcon>
          <ListItemText primary="Invite Doctor" />
        </MenuItem>
        <MenuItem onClick={() => handleAddUser('nurse')}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText primary="Invite Nurse" />
        </MenuItem>
        <MenuItem onClick={() => handleAddUser('patient')}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText primary="Invite Patient" />
        </MenuItem>
        <MenuItem onClick={() => handleAddUser('caregiver')}>
          <ListItemIcon><Support fontSize="small" /></ListItemIcon>
          <ListItemText primary="Invite Caregiver" />
        </MenuItem>
      </Menu>

      {/* Add Agent Menu */}
      <Menu
        anchorEl={addAgentMenuAnchor}
        open={Boolean(addAgentMenuAnchor)}
        onClose={() => setAddAgentMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleAddAgent('clinical')}>
          <ListItemIcon><Science fontSize="small" /></ListItemIcon>
          <ListItemText primary="Clinical Research Agent" />
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('diagnostic')}>
          <ListItemIcon><Analytics fontSize="small" /></ListItemIcon>
          <ListItemText primary="Diagnostic Agent" />
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('support')}>
          <ListItemIcon><Support fontSize="small" /></ListItemIcon>
          <ListItemText primary="Patient Support Agent" />
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('scheduling')}>
          <ListItemIcon><Event fontSize="small" /></ListItemIcon>
          <ListItemText primary="Scheduling Agent" />
        </MenuItem>
      </Menu>

      <Box
        onMouseDown={(event) => handleResizeStart(event, 'right')}
        sx={{
          position: 'absolute',
          top: 58,
          right: 0,
          width: 8,
          height: 'calc(100% - 74px)',
          cursor: 'ew-resize',
          zIndex: 1001,
          bgcolor: 'transparent'
        }}
      />

      <Box
        onMouseDown={(event) => handleResizeStart(event, 'bottom')}
        sx={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          width: 'calc(100% - 16px)',
          height: 8,
          cursor: 'ns-resize',
          zIndex: 1001,
          bgcolor: 'transparent'
        }}
      />

      <Box
        onMouseDown={(event) => handleResizeStart(event, 'corner')}
        sx={{
          position: 'absolute',
          right: 2,
          bottom: 2,
          width: 16,
          height: 16,
          cursor: 'nwse-resize',
          zIndex: 1002,
          '&::before': {
            content: '""',
            position: 'absolute',
            right: 2,
            bottom: 2,
            width: 10,
            height: 10,
            borderRight: '2px solid rgba(25, 118, 210, 0.7)',
            borderBottom: '2px solid rgba(25, 118, 210, 0.7)',
            borderRadius: '1px'
          }
        }}
      />
    </Box>
  );
};

export default LandingChatWindow;